import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap';
import 'react-notifications/lib/notifications.css';

import './App.css';
import './assets/css/all.css';
import './styles/css/HistoryLogin.css';
import './styles/css/Notification.css';
import './styles/scss/style.scss';

import jwt_decode from 'jwt-decode';
import React, { useCallback, useEffect, useMemo } from 'react';
import { NotificationContainer } from 'react-notifications';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';

import {
  fetchAppVersionAndLocale,
  fetchTokenIntervals,
  fetchUsernameGeneratorSignal,
} from './actions/user';
import AdministrationList from './components/Containers/AdministrationList';
import AgGridPage from './components/Containers/AgGridPage';
import AuditEvents from './components/Containers/AuditEvents';
import ChannelList from './components/Containers/ChannelList';
import ComponentDemo from './components/Containers/ComponentDemo';
import Login from './components/Containers/Login';
import PasswordChangeFromLink from './components/Containers/PasswordChangeFromLink';
import PrivacyPolicy from './components/Containers/PrivacyPolicy';
import RoleList from './components/Containers/RoleList';
import UserList from './components/Containers/UserList';
import Dashboard from './components/Dashboard/Dashboard';
import { locationPath } from './constants/app-routes';
import { localStorageConstants } from './constants/globals';
import AuthContext from './context/auth_context';
import store from './reducers/index';
import { securityActions } from './reducers/security';
import './styles/css/HistoryLogin.css';
import './styles/css/Notification.css';
import './styles/scss/style.scss';
import './styles/css/Form.css';
import {
  fetchAppNameFromLocalStorage,
  fetchLocaleFromLocalStorage,
} from './utils';
import { decodeAndSetActions } from './utils/actions';
import NotFoundPage from './utils/security/NotFoundPage';
import SecuredRoute from './utils/security/SecuredRoute';
import TokenChecker from './utils/security/TokenChecker';
import authorizationService, {
  authLogout,
} from './utils/security/authorizationService';
import {
  checkRoute,
  getToken,
  validRoute,
} from './utils/security/security-utils';
import setJWTToken from './utils/security/setJWTToken';

const jwtToken = localStorage.getItem(localStorageConstants.JWT_TOKEN);

if (jwtToken) {
  setJWTToken(jwtToken);
  const decoded_jwtToken = jwt_decode(jwtToken);
  const validTokenCondition =
    !decoded_jwtToken.google2fa ||
    localStorage.getItem(localStorageConstants.GOOGLE_2FA_TOKEN);
  if (
    validTokenCondition &&
    !authorizationService.tokenExpirationCheck() &&
    localStorage.getItem(localStorageConstants.SHOULD_CHANGE_PASSWORD) ===
      'false'
  ) {
    decodeAndSetActions(decoded_jwtToken);
    store.dispatch(securityActions.setCurrentUser(decoded_jwtToken));
  } else {
    authLogout();
  }
}

function App() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { location } = history || {};
  const loggedUserReducer = useSelector((state) => state.loggedUserReducer),
    { allowedActions } = loggedUserReducer || {};

  const USERNAME_GENERATOR =
    localStorage[localStorageConstants.USERNAME_GENERATOR];
  const TOKEN_EXP_CHECK_INTEVRAL = localStorage.getItem(
    localStorageConstants.TOKEN_EXP_CHECK_INTEVRAL
  );
  const REFRESH_INTERVAL = localStorage.getItem(
    localStorageConstants.REFRESH_INTERVAL
  );
  const token = getToken();

  const auth = useMemo(() => loggedUserReducer, [loggedUserReducer]);

  useEffect(() => {
    if (!(fetchAppNameFromLocalStorage() && fetchLocaleFromLocalStorage()))
      dispatch(fetchAppVersionAndLocale());
    if (!USERNAME_GENERATOR) dispatch(fetchUsernameGeneratorSignal());
    if (!TOKEN_EXP_CHECK_INTEVRAL || !REFRESH_INTERVAL)
      dispatch(fetchTokenIntervals());
  }, [
    dispatch,
    token,
    TOKEN_EXP_CHECK_INTEVRAL,
    REFRESH_INTERVAL,
    USERNAME_GENERATOR,
    history,
  ]);

  const checkCurrentRoute = useCallback(
    (route) => {
      !validRoute(route, allowedActions) &&
        history.push(locationPath.DASHBOARD);
    },
    [allowedActions, history]
  );

  useEffect(() => {
    allowedActions?.length > 0 &&
      location.pathname !== locationPath.DASHBOARD &&
      token &&
      checkCurrentRoute(location.pathname);
  }, [checkCurrentRoute, location.pathname, allowedActions, token]);

  return (
    <AuthContext.Provider value={auth}>
      {/* <HistoryLogin /> */}
      <TokenChecker />
      <Switch>
        <Route exact path="/" component={Login} />
        <SecuredRoute
          exact
          path={locationPath.DASHBOARD}
          component={Dashboard}
        />
        <SecuredRoute
          exact
          path={locationPath.USER_LIST}
          component={checkRoute(
            UserList,
            locationPath.USER_LIST,
            allowedActions
          )}
        />
        <SecuredRoute
          exact
          path={locationPath.ROLE_LIST}
          component={checkRoute(
            RoleList,
            locationPath.ROLE_LIST,
            allowedActions
          )}
        />
        <SecuredRoute
          exact
          path={locationPath.CHANNEL_LIST}
          component={checkRoute(
            ChannelList,
            locationPath.CHANNEL_LIST,
            allowedActions
          )}
        />
        <SecuredRoute
          path={locationPath.AUDIT_LOGIN_EVENTS}
          component={checkRoute(
            AuditEvents,
            locationPath.AUDIT_LOGIN_EVENTS,
            allowedActions
          )}
        />
        <SecuredRoute
          path={locationPath.AUDIT_HTTP_TRACE}
          component={checkRoute(
            AuditEvents,
            locationPath.AUDIT_HTTP_TRACE,
            allowedActions
          )}
        />
        <SecuredRoute
          path={locationPath.COMPONENT_DEMO_PAGE}
          component={checkRoute(
            ComponentDemo,
            locationPath.COMPONENT_DEMO_PAGE,
            allowedActions
          )}
        />
        <SecuredRoute
          exact
          path={locationPath.ADMINISTRATION}
          component={checkRoute(
            AdministrationList,
            locationPath.ADMINISTRATION,
            allowedActions
          )}
        />
        <SecuredRoute
          exact
          path={locationPath.PRIVACY_POLICY}
          component={PrivacyPolicy}
        />
        <Route
          exact
          path={locationPath.PASSWORD_CHANGE_FROM_LINK}
          component={PasswordChangeFromLink}
        />
        <Route
          exact
          path={locationPath.CREATE_USER_FROM_LINK}
          component={PasswordChangeFromLink}
        />
        {/* sve rute navoditi iznad aggrid-a */}
        {/* TODO uvesti proveru rute - odnosno obezbediti da se samo aggrid stranice prikazuju kao aggrid!!! */}
        <SecuredRoute exact path={locationPath.AGGRID} component={AgGridPage} />
        <Route path={'/*'} component={NotFoundPage} />
      </Switch>
      <NotificationContainer />
    </AuthContext.Provider>
  );
}

export default App;
